/**
 * Video Status Badge Component
 * Displays the current status of a video with appropriate styling
 */

import { useTranslation } from "react-i18next"

import { CheckCircle, Clock, XCircle } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Spinner } from "@/components/ui/spinner"
import { MediaStatus } from "@/features/studio-ai/types/studio-ai.types"

interface MediaStatusBadgeProps {
  status: MediaStatus
  className?: string
}

export function MediaStatusBadge({ status, className }: MediaStatusBadgeProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.common" })

  const getStatusConfig = () => {
    switch (status) {
      case MediaStatus.Completed:
        return {
          label: t("completed"),
          icon: CheckCircle,
          variant: "success" as const,
          className: "",
        }
      // Sora's `preprocessing` and `running` are generation in progress too;
      // falling through to the default read "Pending" on a running video.
      case MediaStatus.Preprocessing:
      case MediaStatus.Running:
      case MediaStatus.Processing:
        return {
          label: t("processing"),
          icon: Spinner,
          variant: "secondary" as const,
          // Distinguish the active/in-flight state from the muted Cancelled
          // badge below — both use variant="secondary" but Processing needs
          // to read as "working", not "done/dead".
          className: "bg-primary/15 text-primary animate-pulse",
        }
      case MediaStatus.Failed:
        return {
          label: t("failed"),
          icon: XCircle,
          variant: "destructive" as const,
          className: "",
        }
      case MediaStatus.Cancelled:
        return {
          label: t("cancelled"),
          icon: XCircle,
          variant: "secondary" as const,
          className: "",
        }
      case MediaStatus.Pending:
      default:
        return {
          label: t("pending"),
          icon: Clock,
          variant: "outline" as const,
          className: "",
        }
    }
  }

  const config = getStatusConfig()
  const Icon = config.icon

  return (
    <Badge className={`${config.className} ${className}`} variant={config.variant}>
      <Icon className="mr-1 h-3 w-3" />
      {config.label}
    </Badge>
  )
}
